import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import PushPermissionBanner from "@/components/PushPermissionBanner";
import Landing from "@/pages/Landing";
import Browse from "@/pages/Browse";
import ListItem from "@/pages/ListItem";
import MyRentals from "@/pages/MyRentals";
import MyListings from "@/pages/MyListings";
import { CheckoutSuccess, CheckoutCancel } from "@/pages/Checkout";
import AuthPage from "@/pages/AuthPage";
import ProfileSetup from "@/pages/ProfileSetup";
import Terms from "@/pages/Terms";
import NeighborProfile from "@/pages/NeighborProfile";
import Profile from "@/pages/Profile";
import EditProfile from "@/pages/EditProfile";
import Messages from "@/pages/Messages";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Admin from "@/pages/Admin";
import HowItWorks from "@/pages/HowItWorks";

// Small wrapper — the push permission banner is only relevant for signed-in
// users, so it lives under AuthProvider and short-circuits for guests.
const AuthenticatedPushBanner = () => {
    const { isAuthenticated } = useAuth();
    if (!isAuthenticated) return null;
    return <PushPermissionBanner />;
};

const AppRoutes = () => {
    return (
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/browse" element={<Browse />} />
            <Route path="/neighbors/:user_id" element={<NeighborProfile />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/login" element={<AuthPage mode="login" />} />
            <Route path="/signup" element={<AuthPage mode="signup" />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
                path="/auth/callback"
                element={<Navigate to="/login" replace />}
            />
            <Route
                path="/signup/profile"
                element={
                    <ProtectedRoute requireCompleteProfile={false}>
                        <ProfileSetup />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/list"
                element={
                    <ProtectedRoute>
                        <ListItem />
                    </ProtectedRoute>
                }
            />
            <Route path="/list-item" element={<Navigate to="/list" replace />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/checkout/cancel" element={<CheckoutCancel />} />
            <Route
                path="/dashboard/rentals"
                element={
                    <ProtectedRoute>
                        <MyRentals />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/dashboard/listings"
                element={
                    <ProtectedRoute>
                        <MyListings />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/profile"
                element={
                    <ProtectedRoute>
                        <Profile />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/profile/edit"
                element={
                    <ProtectedRoute>
                        <EditProfile />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/messages"
                element={
                    <ProtectedRoute>
                        <Messages />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/messages/:qid"
                element={
                    <ProtectedRoute>
                        <Messages />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin"
                element={
                    <ProtectedRoute>
                        <Admin />
                    </ProtectedRoute>
                }
            />
        </Routes>
    );
};

function App() {
    return (
        <div className="App min-h-screen bg-[#F4F1EA] text-[#2e3220]">
            <BrowserRouter>
                <AuthProvider>
                    <ScrollToTop />
                    <Navbar />
                    <AppRoutes />
                    <AuthenticatedPushBanner />
                    <Footer />
                    <Toaster
                        position="bottom-right"
                        // Slightly longer default duration so the new next-step
                        // descriptions have time to be read. Individual toasts
                        // can still override with { duration: … }.
                        duration={6000}
                        theme="dark"
                        // Force a single visual style for every toast variant
                        // (success/error/info/message). Setting only `style`
                        // leaves Sonner's per-variant description color in
                        // place, which was rendering description text nearly
                        // invisible on our dark-olive background.
                        toastOptions={{
                            classNames: {
                                toast:
                                    "!bg-[#2e3220] !text-[#F4F1EA] !border !border-[#F4F1EA]/10 !shadow-xl",
                                title: "!text-[#F4F1EA] !font-semibold",
                                description: "!text-[#F4F1EA]/85 !text-xs !leading-relaxed",
                                actionButton:
                                    "!bg-[#737a3d] !text-[#F4F1EA]",
                                cancelButton:
                                    "!bg-[#F4F1EA]/10 !text-[#F4F1EA]",
                                closeButton:
                                    "!bg-[#F4F1EA]/10 !text-[#F4F1EA] !border-[#F4F1EA]/20",
                                success: "!text-[#F4F1EA]",
                                error: "!text-[#F4F1EA]",
                                info: "!text-[#F4F1EA]",
                                warning: "!text-[#F4F1EA]",
                            },
                        }}
                    />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
